import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from "framer-motion";
import { AuroraBackground } from "@/components/ui/aurora-background";
import { Check, Search, Users, TrendingUp, Target, Lightbulb, Zap, BarChart, Brain, Workflow, Eye, MapPin } from 'lucide-react';
import { RainbowButton } from "@/components/ui/rainbow-button";
import { PulseBeams } from "@/components/ui/pulse-beams";
import FormModal from "@/components/ui/form-modal";
import Navbar from '@/components/Layout/Navbar';
import Footer from '@/components/Layout/Footer';

const beams = [
  {
    path: "M269 220.5H16.5C10.9772 220.5 6.5 224.977 6.5 230.5V398.5",
    gradientConfig: {
      initial: {
        x1: "0%",
        x2: "0%",
        y1: "80%",
        y2: "100%",
      },
      animate: {
        x1: ["0%", "0%", "200%"],
        x2: ["0%", "0%", "180%"],
        y1: ["80%", "0%", "0%"],
        y2: ["100%", "20%", "20%"],
      },
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "loop",
        ease: "linear",
        repeatDelay: 2,
        delay: Math.random() * 2,
      },
    },
    connectionPoints: [
      { cx: 6.5, cy: 398.5, r: 6 },
      { cx: 269, cy: 220.5, r: 6 }
    ]
  },
  {
    path: "M568 200H841C846.523 200 851 195.523 851 190V40",
    gradientConfig: {
      initial: {
        x1: "0%",
        x2: "0%",
        y1: "80%",
        y2: "100%",
      },
      animate: {
        x1: ["20%", "100%", "100%"],
        x2: ["0%", "90%", "90%"],
        y1: ["80%", "80%", "-20%"],
        y2: ["100%", "100%", "0%"],
      },
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "loop",
        ease: "linear",
        repeatDelay: 2,
        delay: Math.random() * 2,
      },
    },
    connectionPoints: [
      { cx: 851, cy: 34, r: 6.5 },
      { cx: 568, cy: 200, r: 6 }
    ]
  },
  {
    path: "M425.5 274V333C425.5 338.523 421.023 343 415.5 343H152C146.477 343 142 347.477 142 353V426.5",
    gradientConfig: {
      initial: {
        x1: "0%",
        x2: "0%",
        y1: "80%",
        y2: "100%",
      },
      animate: {
        x1: ["20%", "100%", "100%"],
        x2: ["0%", "90%", "90%"],
        y1: ["80%", "80%", "-20%"],
        y2: ["100%", "100%", "0%"],
      },
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "loop",
        ease: "linear",
        repeatDelay: 2,
        delay: Math.random() * 2,
      },
    },
    connectionPoints: [
      { cx: 142, cy: 427, r: 6.5 },
      { cx: 425.5, cy: 274, r: 6 }
    ]
  }
];

const gradientColors = {
  start: "#F59E0B",
  middle: "#EF4444",
  end: "#8B5CF6"
};

const AiConsultation = () => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "AI Business Consultation",
    "description": "Comprehensive AI consultation services to identify automation opportunities, analyze workflows, and create implementation roadmaps for business transformation.",
    "provider": {
      "@type": "Organization",
      "name": "Njord AI Automation Agency",
      "url": "https://www.njordai.co.uk/",
      "telephone": "+447737031147",
      "email": "lennon@njordai.co.uk"
    },
    "serviceType": "AI Consultation Service",
    "category": "Business Consultation",
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "AI Consultation Features",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Workflow Audit",
            "description": "Review processes from admin to sales to service delivery and highlight automation opportunities"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Customer Journey Analysis",
            "description": "Track how customers find, interact, and buy from you to identify AI conversion opportunities"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "AI Enhancement Mapping",
            "description": "Recommend the right AI tools including chatbots, call handling, and automated outreach"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Implementation Roadmap",
            "description": "Step-by-step plan to put AI into action, prioritised by ROI and impact"
          }
        }
      ]
    },
    "offers": {
      "@type": "Offer",
      "description": "AI business consultation starting from £1,500",
      "priceCurrency": "GBP",
      "price": "1500",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "1500",
        "priceCurrency": "GBP",
        "unitText": "consultation"
      }
    },
    "audience": {
      "@type": "BusinessAudience",
      "audienceType": "Businesses seeking AI transformation and automation opportunities"
    }
  };

  return (
    <>
      <Helmet>
        <title>AI Business Consultation | Njord - Discover Your AI Opportunities</title>
        <meta name="description" content="Find out where AI can transform your business. Comprehensive workflow audits, customer journey analysis, and implementation roadmaps. Book your AI consultation today." />
        <meta name="keywords" content="AI consultation, business automation audit, AI strategy, workflow analysis, AI implementation roadmap, business transformation, AI opportunities" />
        <meta property="og:title" content="AI Business Consultation | Njord - Discover Your AI Opportunities" />
        <meta property="og:description" content="Find out where AI can transform your business. Comprehensive workflow audits, customer journey analysis, and implementation roadmaps." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.njordai.co.uk/ai-consultation" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Business Consultation | Njord - Discover Your AI Opportunities" />
        <meta name="twitter:description" content="Find out where AI can transform your business. Comprehensive workflow audits, customer journey analysis, and implementation roadmaps." />
        <link rel="canonical" href="https://www.njordai.co.uk/ai-consultation" />
        
        {/* Service Schema */}
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>
      <Navbar />
      <div className="bg-gradient-to-b from-white to-black">
        <AuroraBackground className="dark h-[80vh]">
          <motion.div
            initial={{ opacity: 0.0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.3,
              duration: 0.8,
              ease: "easeInOut",
            }}
            className="relative flex flex-col gap-4 items-center justify-center px-4 container mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white text-center max-w-3xl">
              Find Out Where AI Can Transform Your Business
            </h1>
            <p className="text-lg md:text-xl text-gray-300 text-center max-w-2xl mb-8">
              Most businesses know AI has potential — but don't know where to apply it. We embed ourselves into your business, analysing workflows and identifying where AI can automate, reduce costs, and increase conversions.
            </p>
            <RainbowButton>
              Book Consultation
            </RainbowButton>
          </motion.div>
        </AuroraBackground>
      </div>

      {/* What We Do Section */}
      <section className="bg-black py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            What We Do
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: <Search className="h-8 w-8 text-primary-300" />,
                title: "Workflow Audit",
                description: "We review your processes — from admin to sales to service delivery — and highlight where automation can save time and money."
              },
              {
                icon: <Users className="h-8 w-8 text-primary-300" />,
                title: "Customer Journey Analysis",
                description: "We track how customers find, interact, and buy from you, identifying opportunities where AI can boost conversions, enquiries, and repeat business."
              },
              {
                icon: <Brain className="h-8 w-8 text-primary-300" />,
                title: "AI Enhancement Mapping",
                description: "Whether it's a chatbot on your website, AI call handling, automated outreach, or site analytics, we recommend the right tools to give you an edge."
              },
              {
                icon: <MapPin className="h-8 w-8 text-primary-300" />,
                title: "Implementation Roadmap",
                description: "You leave with a step-by-step plan to put AI into action, prioritised by ROI and impact."
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-black/50 backdrop-blur-sm p-6 rounded-lg border border-white/10"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                <p className="text-gray-300">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why NjordAI Section */}
      <section className="bg-black py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Why NjordAI?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Target className="h-8 w-8 text-primary-300" />,
                title: "Hands-on, not theoretical",
                description: "We don't just 'talk AI,' we show you how it applies directly to your operations."
              },
              {
                icon: <Eye className="h-8 w-8 text-primary-300" />,
                title: "Cross-industry expertise",
                description: "From manufacturing to retail to professional services, we've seen what works."
              },
              {
                icon: <TrendingUp className="h-8 w-8 text-primary-300" />,
                title: "Conversion-focused",
                description: "We don't just cut costs; we help you win more business with AI-driven websites, funnels, and outreach."
              },
              {
                icon: <Workflow className="h-8 w-8 text-primary-300" />,
                title: "Scalable solutions",
                description: "Start small, grow big — our roadmaps are built for flexibility."
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-black/50 backdrop-blur-sm p-6 rounded-lg border border-white/10 text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-300">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Result Section */}
      <section className="bg-black py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            The Result
          </h2>
          <div className="max-w-3xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                "A clear plan for where to automate and where to enhance",
                "Less wasted time on manual tasks",
                "More efficient teams",
                "Higher conversions and smarter customer engagement"
              ].map((result, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center bg-black/50 backdrop-blur-sm p-4 rounded-lg border border-white/10"
                >
                  <Check className="w-6 h-6 text-primary-300 mr-3 flex-shrink-0" />
                  <span className="text-white font-medium">{result}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="bg-black py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Get Started in 3 Simple Steps
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                icon: <Lightbulb className="h-10 w-10 text-primary-300" />,
                title: "Book Your Consultation",
                description: "Schedule your AI opportunity assessment"
              },
              {
                icon: <Zap className="h-10 w-10 text-primary-300" />,
                title: "We Analyze Your Business",
                description: "Deep dive into your workflows and customer journey"
              },
              {
                icon: <BarChart className="h-10 w-10 text-primary-300" />,
                title: "Get Your AI Roadmap",
                description: "Receive a clear plan prioritized by ROI and impact"
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="relative text-center"
              >
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6">
                  {step.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{step.title}</h3>
                <p className="text-gray-300">{step.description}</p>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2">
                    <div className="w-8 h-0.5 bg-primary-300"></div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FormModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />

      <Footer />
    </>
  );
};

export default AiConsultation;